import React, {FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react';
import {Field, Form} from 'react-final-form';
import axios from 'axios';
import {allCountries} from '@klear/common-utils';
import {KlDialogBody} from "@klear/web-components";

const styles = require('./demoRequest.component.scss');


const CONTACT_URL = '/api/2.1/Contact/demo';

interface DemoForm {
    name: string;
    email: string;
    phone_number: string;
    company: string;
    numOfEmployees: null | string;
    country: null | string;
    receive_marketing: boolean;
}

export interface DemoRequestProps {
    type?: 'startup';
    isDialog?: boolean;
}

declare let dataLayer: any;
declare let mixpanel: any;
declare let isGdpr: boolean;

//TODO Alex - use custom hook later
declare const analytics: {
    track: (eventName: string, params?: Record<string, string | boolean | null>) => Promise<void>;
};

const demoVariants = {
    default: {
        title: 'Schedule Demo',
        subtitle: 'Leave us your details and a Klear expert will be in touch',
        reason: 'Klear Demo Request'
    },
    startup: {
        title: 'Apply To The Klear Startup Plan',
        subtitle: <span>Who's eligible: Companies with less than 20<br/> employees that are not agencies.</span>,
        reason: 'Klear Startup Plan Request'
    }
};

const countryOptions = allCountries.map(country => <option key={country.code} value={country.code}>{country.name}</option>);

const initialForm: DemoForm = {
    name: '',
    email: '',
    phone_number: '',
    company: '',
    numOfEmployees: '1-10',
    country: 'US',
    receive_marketing: false //!isGdpr
};

//temp solution instead of using from webClient
type FieldValidator<T = unknown> = (value: T) => {[validator: string]: boolean}  | undefined | Promise<{[validator: string]: boolean}  | undefined>;

function composeFieldValidators<T>(validators: Array<FieldValidator<T>>): FieldValidator<T> {
    return async value => {
        for (const validator of validators) {
            const validatorError = await validator(value);
            if (validatorError) return validatorError;
        }
        return undefined;
    };
}

const nonBusinessEmailRegex = /^[\w.+\-]+@(g(oogle)?mail|hotmail|live)\..+$/;

const validateBusinessEmail: FieldValidator<string> = value => {
    if (!value) {
        return undefined;
    } else {
        return value.match(nonBusinessEmailRegex) === null ? undefined : {businessEmail: true};
    }
};

const requiredBusinessEmailValidator = composeFieldValidators<string>([validateBusinessEmail]);


export const DemoRequest: FunctionComponent<DemoRequestProps> = props => {
    const {type, isDialog} = props;
    const [requestSent, setRequestSent] = useState(false);
    const [localStorageFirstSource, setLocalStorageFirstSource] = useState<string | null>(null);
    const [localStorageFinalSource, setLocalStorageFinalSource] = useState<string | null>(null);

    useEffect( () => {
        if (typeof window != "undefined" && window.localStorage ) {
            const url = new URL(window.location.href);
            const searchParams = new URLSearchParams(url.search);
            if (searchParams.get('utm_source') !== null) {
                localStorage.setItem('final_source', searchParams.get('utm_source') as string);
                if (localStorage.getItem('first_source') === null) {
                    localStorage.setItem('first_source', searchParams.get('utm_source') as string);
                }
            }
            setLocalStorageFirstSource(window.localStorage.getItem('first_source'));
            setLocalStorageFinalSource(window.localStorage.getItem('final_source'));
        }
    },[typeof window]);

    const handleSegmentSubmit = useCallback((demoRequest: DemoForm) => {
        if(typeof analytics !== 'undefined'){
            analytics.track(`Demo Request Sent`, {
                category: 'Demo Request',
                label: document.title,
                lead_name: demoRequest.name,
                lead_email: demoRequest.email,
                lead_phone_number: demoRequest.phone_number,
                lead_company: demoRequest.company,
                lead_num_of_employees: demoRequest.numOfEmployees,
                lead_country: demoRequest.country,
                lead_receive_marketing: demoRequest.receive_marketing
            });
        }
    },[]);

    const params = useMemo(() => {
        if (typeof window != "undefined") {
            const url = new URL(window.location.href);
            const searchParams = new URLSearchParams(url.search);
            return {
                source: searchParams.get('utm_source'),
                medium: searchParams.get('utm_medium'),
                campaign: searchParams.get('utm_campaign'),
                term: searchParams.get('utm_term'),
                content: searchParams.get('utm_content')
            };
        }
        return {};
    }, [typeof window]);

    const handleSubmit = (demoRequest: DemoForm) => {
        const variant = demoVariants[type || 'default'] || {};
        const postParams = {
            utm_source: localStorageFirstSource || params.source || undefined,
            utm_campaign: params.campaign || undefined,
            utm_medium: params.medium || undefined,
            utm_term: params.term || undefined,
            utm_content: params.content || undefined,
            final_source: localStorageFinalSource || undefined,
            ...demoRequest};
        axios.post(CONTACT_URL, {
            reason: variant.reason,
            params: {...postParams},
        }).then(() => {
            handleSegmentSubmit(demoRequest);
            setRequestSent( true);
            if (typeof dataLayer !== 'undefined') {
                dataLayer.push({
                    event: 'demoRequestSent',
                    currentLocation: location.pathname
                });
            }
            if(typeof mixpanel !== 'undefined'){
                mixpanel.alias(demoRequest.email);
                mixpanel.track("Demo Request Sent");
            }
        }).catch(err => {
            alert('error!' + err);
        });
    };

    const submitPartialForm = useCallback((partialDemoRequest: DemoForm) => {
        if(typeof analytics !== 'undefined'){
            analytics.track(`Partial Demo Request`, {
                category: 'Demo Request',
                label: document.title,
                lead_name: partialDemoRequest.name,
                lead_email: partialDemoRequest.email,
                lead_phone_number: partialDemoRequest.phone_number,
                lead_company: partialDemoRequest.company,
                lead_num_of_employees: partialDemoRequest.numOfEmployees,
                lead_country: partialDemoRequest.country,
                lead_receive_marketing: partialDemoRequest.receive_marketing
            });
        }
    },[]);

        const variant = demoVariants[type || 'default'] || {};
        return (
            <div className={`${styles.klearProfilesModal} ${isDialog ? styles.dialogMobile : ''}`}>
                <div className={styles.fancyBoxContent}>
                    <KlDialogBody noPadding className={styles.modalContainer}>
                        {!requestSent ? <Form onSubmit={handleSubmit} initialValues={initialForm}>
                            {({handleSubmit, values, invalid}) => <form id="demoForm2" method='get' onSubmit={handleSubmit}>
                               <div className={styles.modalTop}>
                                    <div className={styles.titles}>
                                        <div className={styles.largeTitle}>{variant.title}</div>
                                        <div className={styles.smallTitle}>{variant.subtitle}</div>
                                    </div>
                                </div>
                                <fieldset className={styles.fieldsetContainer}>
                                    <div className={styles.allRows}>
                                        <div className={styles.demoRow}>
                                            <label className={styles.demoLabel}>*Name:</label>
                                            <Field name="name">
                                                {({input}) =>
                                                    <input {...input} tabIndex={1} placeholder='Full Name'
                                                           type="text" className={styles.demoForm} required
                                                           onBlur={e => {
                                                               submitPartialForm(values);
                                                               input.onBlur(e);
                                                           }}/>
                                                }
                                            </Field>
                                        </div>
                                        <div className={styles.demoRow}>
                                            <Field name="email" validate={requiredBusinessEmailValidator}>
                                                {({input, meta}) => (
                                                    <>
                                                        <div className={styles.labelRow}>
                                                            <label className="demoLabel">*Work Email:</label>
                                                            {!!meta.invalid && !!meta.touched && !!meta.dirty && !meta.active &&
                                                                <span className={styles.error}>Please use work email</span>
                                                            }
                                                        </div>
                                                        <input {...input} tabIndex={2} placeholder='Work Email' type="email"
                                                               className={styles.demoForm} required
                                                               onBlur={e => {
                                                                   submitPartialForm(values);
                                                                   input.onBlur(e);
                                                               }}/>
                                                    </>
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className={styles.demoRow}>
                                            <label>*Phone:</label>
                                            <Field name="phone_number">
                                                {({input}) =>
                                                    <input {...input} tabIndex={3}
                                                           placeholder='Phone Number'
                                                           type="text" className={styles.demoForm} required
                                                           onBlur={e => {
                                                               submitPartialForm(values);
                                                               input.onBlur(e);
                                                           }}/>
                                                }
                                            </Field>
                                        </div>
                                        <div className={styles.demoRow}>
                                            <label>*Company:</label>
                                            <Field name="company">
                                                {({input}) =>
                                                    <input {...input} tabIndex={4} placeholder='Company Name'
                                                           type="text" className={styles.demoForm} required
                                                           onBlur={e => {
                                                               submitPartialForm(values);
                                                               input.onBlur(e);
                                                           }}/>
                                                }
                                            </Field>
                                        </div>
                                        <div className={styles.demoRow}>
                                            <div className="form-group">
                                                <label># Employees:</label>
                                                <div className="dropdown">
                                                    <Field name="numOfEmployees">
                                                        {({input}) =>
                                                            <select {...input} tabIndex={5}
                                                                    style={{height: '42px', fontFamily: 'Raleway'}}
                                                                    className={`form-control ${styles.demoForm} clickable`}
                                                                    required
                                                                    onBlur={e => {
                                                                        submitPartialForm(values);
                                                                        input.onBlur(e);
                                                                    }}>
                                                                <option>1-10</option>
                                                                <option>10-100</option>
                                                                <option>100-1000</option>
                                                                <option>1000+</option>
                                                            </select>
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.demoRow}>
                                            <div className="form-group">
                                                <label>Country:</label>
                                                <div className="dropdown">
                                                    <Field name="country">
                                                        {({input}) =>
                                                            <select {...input} tabIndex={6}
                                                                    style={{height: '42px', fontFamily: 'Raleway', paddingBottom: '10px'}}
                                                                    className={`form-control ${styles.demoForm} clickable`}
                                                                    required
                                                                    onBlur={e => {
                                                                        submitPartialForm(values);
                                                                        input.onBlur(e);
                                                                    }}>
                                                                {countryOptions}
                                                            </select>
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                        </div>
                                        {false && isGdpr && <div className="demoRow">
                                            <div className="form-group">
                                                <label>
                                                    <Field name="receive_marketing">
                                                        {({input}) =>
                                                            <input {...input} tabIndex={7}
                                                                   type="checkbox" required
                                                                   onBlur={e => {
                                                                       submitPartialForm(values);
                                                                       input.onBlur(e);
                                                                   }}/>
                                                        }
                                                    </Field>
                                                    I don't know
                                                </label>
                                            </div>
                                        </div>}
                                        <div className="clear"/>
                                    </div>
                                    <input type="hidden" name="whatToDo" value="send"/>
                                    <div className={styles.cta}>
                                        <input className={styles.butt2020} value="Request Demo"
                                               tabIndex={8} type="submit" disabled={invalid}/>
                                        <div className="clear"/>
                                    </div>
                                </fieldset>
                            </form>}
                        </Form> :
                        <div className={styles.thankyouNote2}>
                            <div className={styles.title}>Great! It won't be long now.</div>
                            <div className={styles.subTitle}>A Klear specialist will be in touch soon.</div>
                            <div className={styles.animation}>
                                {/*{officeAnimation}*/}
                                <img src={"/images/new/officeAnimation.svg"} alt={''}/>
                            </div>
                        </div>}
                    </KlDialogBody>

                </div>
            </div>
        );
};
